import { useState } from "react";
import { useDriver } from "../../api/admin/driver";
import ApiError from "../../api/common/apiError";
import {
    type ApiDriverDetailsResponse,
    type ApiDriversListResponse,
} from "../../models/api/admin/driver";
import { errorToast, successToast } from "../../toast";
import FormMessageBox from "../FormMessageBox";
import PaginationNav from "../PaginationNav";
import Table, { HiddenCols } from "../ReusableComponents/Table";
import { driverListMenuGroups, initTableContents } from "./DriverListInit";
import {
    DriverListMenuKeys,
    driverListMenuItems,
    driverListOrderByOptions,
} from "./DriverListTableOptions";
import { appPaths } from "../../Routes";
import { useNavigate } from "react-router-dom";
import DeleteConfirmation from "../ReusableComponents/DeleteConfirmation";
import { type ApiCompanySummary } from "../../models/api/admin/company";
import { useCompany } from "../../api/admin/company";
import {
    tableHeaderOptionsEnum,
    TableHeaderOptionsType,
    useTableHeaders,
} from "../../hooks/useTableHeaders";
import {
    driverCategoryOptionsEn,
    type DriverCategoryOptionsType,
} from "../../utils/driverPageLocation";
import useTableStore from "../../stores/useTableStore";

export type DriverListTableProps = {
    location: DriverCategoryOptionsType;
    isLoading: boolean;
    error: ApiError | undefined;
    driverList?: ApiDriversListResponse;
    setPageNumber: (value: number) => void;
    pageSize: number;
    setPageSize: (value: number) => void;
    sortOrder: "asc" | "desc";
    setSortOrder: (val: string) => void;
    orderBy: string;
    setOrderBy: (val: string) => void;
    handleRefetchDriverList: () => void;
    tableRef: React.MutableRefObject<HTMLDivElement | null>;
    selectedCompany?: ApiCompanySummary;
};

const DriverListTable: React.FC<DriverListTableProps> = ({
    location,
    isLoading,
    error,
    driverList,
    setPageNumber,
    pageSize,
    setPageSize,
    sortOrder,
    setSortOrder,
    orderBy,
    setOrderBy,
    handleRefetchDriverList,
    tableRef,
    selectedCompany,
}) => {
    const tableIdentifier: TableHeaderOptionsType =
        location === driverCategoryOptionsEn.drivers
            ? tableHeaderOptionsEnum.allDrivers
            : tableHeaderOptionsEnum.professionalDrivers;
    const { setTableHeaders, setHiddenCols } = useTableHeaders(tableIdentifier);
    const tableHeaders = useTableStore((state) =>
        state.getTableHeaders(tableIdentifier)
    );
    const hiddenCols = useTableStore((state) =>
        location === driverCategoryOptionsEn.drivers
            ? state.hiddenColsAllDrivers
            : state.hiddenColsProfessionalDrivers
    );

    const navigate = useNavigate();

    const { deleteDriver, isDeletingDriver } = useDriver();
    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedDriver, setSelectedDriver] = useState<
        ApiDriverDetailsResponse | undefined
    >();

    const handleDeleteDriver = async () => {
        const id = selectedDriver?.driverId;
        if (!id) {
            errorToast("There was an error");
            return;
        }
        const response = await deleteDriver(id);

        if (response.success) {
            successToast("Driver deleted successfully");
            handleRefetchDriverList();
            setDeleteDialogOpen(false);
        }
    };

    const handleTableMenuSelect = (
        data: ApiDriverDetailsResponse,
        key: DriverListMenuKeys
    ) => {
        setSelectedDriver(data);
        const id = data.driverId;
        switch (key) {
            case DriverListMenuKeys.editDriver:
                navigate(appPaths.adminEditDriver(id));
                break;
            case DriverListMenuKeys.deleteDriver:
                setDeleteDialogOpen(true);
                break;
        }
    };

    const handleSortColumn = (newOrderBy?: string) => {
        if (!newOrderBy) return;
        if (orderBy === newOrderBy) {
            if (sortOrder === "asc") {
                setSortOrder("desc");
            } else {
                setSortOrder("asc");
            }
        }
        setOrderBy(newOrderBy);
    };

    /** If selectedCompany exists
     * fetch company so we can get correct risk profiles for table.
     *
     * Delays loading time of table.
     */
    const { companyData, isLoadingCompany } = useCompany(
        selectedCompany?.companyId
    );

    const waitingForRiskProfile =
        !!selectedCompany?.companyId && isLoadingCompany;

    const tableContents = initTableContents(
        driverList?.items,
        companyData?.companyRiskProfiles
    );

    const handleHiddenCols = (value: HiddenCols[]) => {
        const option =
            location === driverCategoryOptionsEn.drivers
                ? tableHeaderOptionsEnum.allDrivers
                : tableHeaderOptionsEnum.professionalDrivers;
        setHiddenCols(value, option);
    };

    return (
        <>
            {error && (
                <FormMessageBox
                    title={"error"}
                    message={
                        <span>
                            There was an error loading driver data.
                            <br />
                            Please try again in 10 minutes.
                        </span>
                    }
                    variant={"error"}
                />
            )}
            <Table
                tableHeaders={tableHeaders}
                setTableHeaders={(value) =>
                    setTableHeaders(value, tableIdentifier)
                }
                tableContents={tableContents}
                menuItems={(data: ApiDriverDetailsResponse) =>
                    driverListMenuItems(data, handleTableMenuSelect)
                }
                menuGroups={driverListMenuGroups}
                hiddenCols={hiddenCols}
                setHiddenCols={handleHiddenCols}
                orderByOptions={driverListOrderByOptions}
                orderBy={orderBy}
                sortOrder={sortOrder}
                handleSortColumn={handleSortColumn}
                isLoading={isLoading || waitingForRiskProfile}
                tableRef={tableRef}
            />

            {driverList && (
                <>
                    {driverList?.items.length === 0 && !isLoading && (
                        <div className="rounded border-2 border-S2D-error-40 bg-S2D-error-90 p-4 text-S2D-neutral-10">
                            No drivers found
                        </div>
                    )}
                    <PaginationNav
                        setPageNumber={setPageNumber}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        totalCount={driverList?.totalCount}
                        totalPages={driverList?.totalPages}
                        currentPage={driverList?.pageNumber}
                        hasPreviousPage={driverList?.hasPreviousPage}
                        hasNextPage={driverList?.hasNextPage}
                    />
                    <DeleteConfirmation
                        isOpen={isDeleteDialogOpen}
                        onClose={() => setDeleteDialogOpen(false)}
                        title={"You are about to delete a driver!"}
                        onSubmit={handleDeleteDriver}
                        isLoading={isDeletingDriver}
                    />
                </>
            )}
        </>
    );
};

export default DriverListTable;
